import { z } from 'zod';
/* ------------------------------------------------------------------ enums */
/**
 * What the system lets you do. A permission ladder, nothing more.
 * Who *decides* what is a separate axis — see `Authority`.
 */
export const Role = z.enum(['owner', 'member', 'viewer']);
/**
 * What a person is the decider for, inside one project.
 *
 * Deliberately orthogonal to `Role`, because the real structure is not a ladder:
 * the developer has the last word on implementation and the sales engineer has it
 * on requirements, and neither outranks the other. Modelling that as owner/member
 * would put one of them above the other and be wrong.
 *
 * A person can hold several, and a project can have an area nobody holds — which is
 * itself worth surfacing, since questions in that area have nowhere to go.
 */
export const Authority = z.enum([
    'implementation', // 実装の最終判断
    'requirements', // 要件の最終判断
    'schedule', // 期限のコミット
    'contract', // 契約上の判断
    'acceptance', // 成果物の検収
]);
export const AUTHORITY_LABEL = {
    implementation: '実装',
    requirements: '要件',
    schedule: '期限',
    contract: '契約',
    acceptance: '検収',
};
export const AUTHORITY_DESCRIPTION = {
    implementation: '実装方針・技術的な実現方法の最終判断',
    requirements: '要件の中身・仕様の最終判断',
    schedule: '期限のコミットとスケジュールの判断',
    contract: '契約範囲かどうかの判断',
    acceptance: '成果物の検収',
};
export const AuthorType = z.enum(['human', 'agent']);
/**
 * Who, of a recipient, is being addressed.
 *
 * Each person is one identity with a Claude acting for them, so an agent is not a
 * separate participant — it holds no authority of its own and would otherwise need
 * its own token, role and lifecycle. What it does need is to be *addressable*, and
 * the distinction carries real behaviour: a question put to someone's Claude may be
 * answered without waiting for them, while one put to the person must not be
 * answered on their behalf.
 */
export const MessageTarget = z.enum(['human', 'agent', 'any']);
export const MessageKind = z.enum(['chat', 'question', 'answer', 'decision', 'system']);
export const DraftKind = z.enum(['session_summary', 'doc_change', 'message']);
export const DraftStatus = z.enum(['pending', 'approved', 'rejected']);
export const ReportStatus = z.enum(['working', 'blocked', 'done']);
/* --------------------------------------------------------------- findings */
export const RedactionFinding = z.object({
    rule: z.string(),
    severity: z.enum(['block', 'warn']),
    label: z.string(),
    excerpt: z.string(),
    index: z.number().int(),
});
/* --------------------------------------------------------- people & rooms */
/**
 * A person, independent of any project.
 *
 * The token authenticates a *person*, not a membership: the same people work
 * together across several projects at once, and issuing one token per project
 * would mean re-inviting everyone every time a project starts.
 */
export const Person = z.object({
    person_id: z.string(),
    display_name: z.string(),
    org: z.string(),
    /**
     * What this person calls their Claude. Shown as `アルフレッド(成田の Claude)` so the
     * persona is visible without ever obscuring whose agent it is — across a company
     * boundary, "who said this" has to stay unambiguous.
     */
    agent_nickname: z.string().nullable(),
    created_at: z.string(),
    last_seen_at: z.string().nullable(),
    /** When this person's bridge last held a live connection. */
    agent_last_seen_at: z.string().nullable(),
    /** Which version of the participation rules they accepted, and when. */
    consent_version: z.string().nullable(),
    consented_at: z.string().nullable(),
});
/**
 * What a person allows their own Claude to do in one project.
 *
 * These are the member's own choices, not the owner's: an owner must never be able
 * to make someone else's agent speak. Defaults keep the system useful out of the
 * box, except `auto_publish_summaries`, which weakens the approval gate and is
 * therefore off until deliberately turned on.
 */
export const MemberSettings = z.object({
    /** When false the agent may only draft; the person sends. */
    agent_may_post: z.boolean(),
    /** When false nothing is injected automatically; the agent reads only on request. */
    agent_may_read: z.boolean(),
    /** When true an approved-looking summary publishes without review. */
    auto_publish_summaries: z.boolean(),
    /** When true the hub acknowledges questions while this person's agent is offline. */
    auto_reply_when_away: z.boolean(),
});
export const DEFAULT_MEMBER_SETTINGS = {
    agent_may_post: true,
    agent_may_read: true,
    auto_publish_summaries: false,
    auto_reply_when_away: true,
};
export const RoomSummary = z.object({
    id: z.string(),
    name: z.string(),
    role: Role,
    archived: z.boolean(),
    /**
     * The caller's own settings for this project. Carried here so the hooks — which
     * run every turn — can honour them without a second request.
     */
    settings: MemberSettings,
    /** Newest message timestamp, so a client can order by recency. */
    last_activity_at: z.string().nullable(),
});
export const CreateRoomRequest = z.object({
    name: z.string().min(1).max(120),
});
export const MeResponse = z.object({
    person: Person,
    rooms: z.array(RoomSummary),
});
/* ---------------------------------------------------------------- members */
/**
 * A person as seen inside one room. `user_id` is the person's id — the same value
 * across every room they belong to — so message authorship stays stable while
 * `role` varies per project.
 */
/**
 * How long ago something happened, rounded to days.
 *
 * Deliberately coarse. The point of showing activity across a company boundary is
 * "there is work happening that has not been reported", not "they logged in at
 * 02:14". Anything finer reads as surveillance and would get this switched off —
 * and a week is the point past which the exact number stops changing what anyone
 * would do about it.
 */
export const ActivityAge = z.enum(['today', 'yesterday', 'days_2', 'days_3', 'days_4', 'days_5', 'days_6', 'week_plus', 'never']);
export const ACTIVITY_AGE_LABEL = {
    today: '本日',
    yesterday: '昨日',
    days_2: '2日前',
    days_3: '3日前',
    days_4: '4日前',
    days_5: '5日前',
    days_6: '6日前',
    week_plus: '1週間以上前',
    never: 'なし',
};
/**
 * What the room can see about someone's participation without seeing any content.
 *
 * `unshared` is the signal that matters: it means this person has been working on
 * the project since the last time they shared anything. It says nothing about what
 * they did — the point is to give a PM a reason to ask, not an answer.
 */
export const MemberActivity = z.object({
    worked: ActivityAge,
    shared: ActivityAge,
    unshared: z.boolean(),
});
export const Member = z.object({
    user_id: z.string(),
    display_name: z.string(),
    org: z.string(),
    agent_nickname: z.string().nullable(),
    role: Role,
    /** Decision areas this person owns in this project. May be empty. */
    authorities: z.array(Authority),
    settings: MemberSettings,
    /** Whether this person's bridge is connected right now. */
    agent_online: z.boolean(),
    agent_last_seen_at: z.string().nullable(),
    created_at: z.string(),
    last_seen_at: z.string().nullable(),
    /** Coarse participation signal — see `MemberActivity`. Never carries content. */
    activity: MemberActivity,
});
/**
 * Enough to draw a project card on the landing page without opening the project.
 *
 * Separate from `GET /state` on purpose: fetching state marks published summaries as
 * delivered, and simply glancing at a list of projects must not quietly do that to
 * every one of them.
 */
export const RoomCard = z.object({
    id: z.string(),
    name: z.string(),
    role: Role,
    archived: z.boolean(),
    member_count: z.number().int(),
    /** Unresolved questions in the project, and the subset this person has to answer. */
    open_questions: z.number().int(),
    questions_for_me: z.number().int(),
    pending_for_me: z.number().int(),
    /** This person's own participation — see `MemberActivity`. */
    activity: MemberActivity,
    last_activity_at: z.string().nullable(),
});
export const UpdateMemberRequest = z.object({
    role: Role.optional(),
    authorities: z.array(Authority).optional(),
});
/** A person changing their own name and agent behaviour. Never applied to anyone else. */
export const UpdateMySettingsRequest = z.object({
    settings: MemberSettings.partial().optional(),
    agent_nickname: z.string().max(40).nullable().optional(),
    /** Your own display name. Resolved on read, so history updates with it. */
    display_name: z.string().min(1).max(80).optional(),
});
/* --------------------------------------------------------------- messages */
export const Message = z.object({
    id: z.string(),
    room_id: z.string(),
    /** Monotonic within a room. Clients track only this to resume. */
    seq: z.number().int(),
    author_type: AuthorType,
    /** The member this came from. For agents, the member whose session it runs in. */
    author_id: z.string(),
    author_name: z.string(),
    author_org: z.string(),
    kind: MessageKind,
    body: z.string(),
    in_reply_to: z.string().nullable(),
    /** user_ids this is addressed to. Empty means the whole room. */
    mentions: z.array(z.string()),
    /** Whether the recipients' people, their Claudes, or either are being asked. */
    to_role: MessageTarget,
    /**
     * Posted by the hub on someone's behalf while they were away, rather than written
     * by them or by their Claude in a live session. Always marked, because its author
     * has not read it.
     */
    automated: z.boolean(),
    /** Display label for the author, including the agent nickname when there is one. */
    author_label: z.string(),
    /** For questions: which decision area an answer has to come from. */
    domain: Authority.nullable(),
    resolved_at: z.string().nullable(),
    resolved_by: z.string().nullable(),
    /**
     * Set when the author took it back after somebody had already received it. The
     * body is emptied and this marker is left in its place: the other side saw
     * something, so pretending nothing was ever there would be a lie. A retraction
     * nobody had received yet deletes the row outright and never reaches this field.
     */
    retracted_at: z.string().nullable(),
    created_at: z.string(),
});
export const PostMessageRequest = z.object({
    body: z.string().min(1).max(64_000),
    kind: MessageKind.default('chat'),
    /** Set by the local bridge when Claude is speaking rather than the human. */
    as_agent: z.boolean().default(false),
    in_reply_to: z.string().nullable().default(null),
    mentions: z.array(z.string()).default([]),
    /**
     * Address the person, their Claude, or either. `human` is what stops an agent
     * answering something only its operator can decide.
     */
    to_role: MessageTarget.default('any'),
    /**
     * For questions: the decision area. When set and `mentions` is empty, the hub
     * addresses it to whoever holds that authority — which is what removes the human
     * from the routing decision, not just from the forwarding.
     */
    domain: Authority.nullable().default(null),
});
/**
 * Posting can end two ways: it goes out, or the outbound scanner held it for a
 * human to approve. Callers must handle both — see `redact.ts`.
 */
export const PostMessageResult = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('posted'),
        message: Message,
        warnings: z.array(RedactionFinding),
    }),
    z.object({
        status: z.literal('held'),
        draft_id: z.string(),
        findings: z.array(RedactionFinding),
        reason: z.string(),
    }),
    /**
     * The same question, still unanswered, was asked moments ago — so this one was
     * not added. Carries the existing message rather than an error: from the caller's
     * point of view the question is asked, which is what they wanted.
     */
    z.object({
        status: z.literal('duplicate'),
        message: Message,
    }),
]);
export const MessagePage = z.object({
    messages: z.array(Message),
    latest_seq: z.number().int(),
});
export const AnswerRequest = z.object({
    body: z.string().min(1).max(64_000),
    as_agent: z.boolean().default(false),
    /** Mark the question resolved. Humans judge; agents usually leave this false. */
    resolve: z.boolean().default(false),
});
/* ----------------------------------------------------------------- drafts */
export const SessionSummaryPayload = z.object({
    kind: z.literal('session_summary'),
    session_key: z.string(),
    title: z.string().max(200),
    status: ReportStatus,
    body: z.string().max(32_000),
});
export const DocChangePayload = z.object({
    kind: z.literal('doc_change'),
    path: z.string().min(1).max(400),
    content: z.string().max(1_000_000),
    note: z.string().max(2_000).default(''),
    /** S3 version the edit was based on; null when creating the doc. */
    base_version: z.string().nullable().default(null),
});
export const MessagePayload = z.object({
    kind: z.literal('message'),
    body: z.string(),
    message_kind: MessageKind,
    in_reply_to: z.string().nullable(),
    mentions: z.array(z.string()),
    as_agent: z.boolean(),
});
export const DraftPayload = z.discriminatedUnion('kind', [
    SessionSummaryPayload,
    DocChangePayload,
    MessagePayload,
]);
export const Draft = z.object({
    id: z.string(),
    room_id: z.string(),
    kind: DraftKind,
    author_id: z.string(),
    author_name: z.string(),
    /** True when the local bridge created it on Claude's behalf. */
    from_agent: z.boolean(),
    title: z.string(),
    payload: DraftPayload,
    findings: z.array(RedactionFinding),
    status: DraftStatus,
    created_at: z.string(),
    decided_by: z.string().nullable(),
    decided_at: z.string().nullable(),
    reject_reason: z.string().nullable(),
});
export const ApproveDraftRequest = z.object({
    /** Approver may rewrite the content before it becomes visible to the other side. */
    payload: DraftPayload.optional(),
});
export const RejectDraftRequest = z.object({
    reason: z.string().max(2_000).default(''),
});
/* ---------------------------------------------------------------- reports */
export const SessionReport = z.object({
    id: z.string(),
    room_id: z.string(),
    author_id: z.string(),
    author_name: z.string(),
    author_org: z.string(),
    session_key: z.string(),
    title: z.string(),
    status: ReportStatus,
    body: z.string(),
    /** See `Message.retracted_at` — same rule, same reason. */
    retracted_at: z.string().nullable(),
    updated_at: z.string(),
});
/**
 * What a retraction actually achieved.
 *
 * `purged` means nobody else had received it and the content is gone. `tombstoned`
 * means somebody had, so a marker remains and `seen_by` names them. The distinction
 * is the whole point: the person taking something back needs to know whether they
 * got there in time, not a reassuring "deleted" either way.
 */
export const RetractResult = z.object({
    status: z.enum(['purged', 'tombstoned']),
    seen_by: z.array(z.object({ person_id: z.string(), display_name: z.string(), org: z.string() })),
});
export const PostReportRequest = z.object({
    session_key: z.string().min(1),
    title: z.string().min(1).max(200),
    status: ReportStatus.default('working'),
    body: z.string().min(1).max(32_000),
    /**
     * The author has already read this text and said to send it.
     *
     * Set by the local bridge after it has shown the summary and the scan results to
     * the person and they answered. It skips the approval queue — the review already
     * happened, in the place where they were actually looking.
     *
     * The hub does not take this as permission to skip its own scan. Anything with a
     * credential-level finding is still held no matter what the client claims: the
     * bridge runs on someone's laptop and can be edited, so it cannot be the thing
     * that decides what leaves the company.
     */
    confirmed: z.boolean().default(false),
});
/* ------------------------------------------------------------------- docs */
export const DocMeta = z.object({
    path: z.string(),
    summary: z.string(),
    updated_by: z.string(),
    updated_by_name: z.string(),
    updated_at: z.string(),
    version_id: z.string(),
    /** Pending doc_change drafts targeting this path. */
    proposed_count: z.number().int(),
});
export const DocContent = DocMeta.extend({ content: z.string() });
export const DocVersion = z.object({
    version_id: z.string(),
    author_id: z.string(),
    author_name: z.string(),
    approved_by: z.string().nullable(),
    note: z.string(),
    created_at: z.string(),
});
export const ProposeDocRequest = z.object({
    content: z.string().max(1_000_000),
    note: z.string().max(2_000).default(''),
    base_version: z.string().nullable().default(null),
    as_agent: z.boolean().default(false),
});
export const DocDiff = z.object({
    path: z.string(),
    from: z.string().nullable(),
    to: z.string().nullable(),
    patch: z.string(),
});
/* -------------------------------------------------------------- decisions */
export const Decision = z.object({
    id: z.string(),
    room_id: z.string(),
    question_message_id: z.string().nullable(),
    question_excerpt: z.string().nullable(),
    decided_by: z.string(),
    decided_by_name: z.string(),
    decision: z.string(),
    rationale: z.string(),
    domain: Authority.nullable(),
    /**
     * False when the decider did not hold the area they decided in. Recorded rather
     * than blocked: sometimes it is the right call to make, but across a company
     * boundary it is exactly the thing people argue about afterwards.
     */
    within_authority: z.boolean(),
    created_at: z.string(),
});
export const PostDecisionRequest = z.object({
    question_message_id: z.string().nullable().default(null),
    decision: z.string().min(1).max(4_000),
    rationale: z.string().max(8_000).default(''),
    /** Defaults to the area of the question being answered, when there is one. */
    domain: Authority.nullable().default(null),
});
/* ------------------------------------------------------------- room state */
/**
 * How to name someone's Claude, everywhere.
 *
 * Kept here rather than in each view because it was being assembled separately in
 * the timeline, the recipient picker, the board, the admin screen and the CLI —
 * and the ones that had not been updated quietly dropped the nickname. Whose agent
 * is speaking is the one thing a reader must never have to guess, so the display
 * name stays in the label even when a nickname is set; the nickname is added to it,
 * never substituted for it.
 */
export function agentLabel(displayName, nickname) {
    return nickname ? `${nickname}(${displayName} の Claude)` : `${displayName} の Claude`;
}
/* ------------------------------------------------------------ environments */
/**
 * The environments a project actually runs in.
 *
 * Kept as first-class rows rather than left to chat, because on a web or app
 * engagement these are asked for constantly — "STG の URL どれでしたっけ" — and the
 * answer scrolls away. They also change on every redeploy, so the question is
 * really "what is the current one", which a chat log answers badly.
 */
export const LinkKind = z.enum(['dev', 'stg', 'prod', 'apk', 'ios', 'other']);
export const LINK_KIND_LABEL = {
    dev: 'DEV',
    stg: 'STG',
    prod: 'PROD',
    apk: 'APK',
    ios: 'iOS',
    other: 'その他',
};
/** Kinds that are handed to a phone, where a QR code is the practical way in. */
export const QR_KINDS = ['apk', 'ios'];
export const EnvLink = z.object({
    id: z.string(),
    room_id: z.string(),
    kind: LinkKind,
    label: z.string(),
    url: z.string(),
    note: z.string().nullable(),
    updated_by: z.string(),
    updated_by_name: z.string(),
    updated_at: z.string(),
});
export const UpsertLinkRequest = z.object({
    /** Omitted to add; present to replace. */
    id: z.string().optional(),
    kind: LinkKind,
    label: z.string().min(1).max(80),
    url: z.string().url().max(2000),
    note: z.string().max(500).nullable().default(null),
});
/* ------------------------------------------------------------ attachments */
/**
 * What may be attached, and whether it can be checked before it leaves.
 *
 * The split is the whole point. Text formats go through the same outbound scan as
 * everything else, so a password in a CSV is caught. A .docx or a .pdf cannot be
 * read by that scanner, so nothing is checked — and the honest thing is to say so
 * on the file itself rather than let the presence of a scanner elsewhere imply a
 * guarantee that does not hold here.
 */
export const ATTACHMENT_TYPES = {
    csv: { mime: 'text/csv', text: true },
    json: { mime: 'application/json', text: true },
    txt: { mime: 'text/plain', text: true },
    md: { mime: 'text/markdown', text: true },
    log: { mime: 'text/plain', text: true },
    yml: { mime: 'text/yaml', text: true },
    yaml: { mime: 'text/yaml', text: true },
    xml: { mime: 'application/xml', text: true },
    svg: { mime: 'image/svg+xml', text: true },
    pdf: { mime: 'application/pdf', text: false },
    png: { mime: 'image/png', text: false },
    jpg: { mime: 'image/jpeg', text: false },
    jpeg: { mime: 'image/jpeg', text: false },
    gif: { mime: 'image/gif', text: false },
    webp: { mime: 'image/webp', text: false },
    docx: {
        mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        text: false,
    },
    xlsx: { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', text: false },
    pptx: {
        mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        text: false,
    },
    zip: { mime: 'application/zip', text: false },
};
/** 25 MB. Big enough for a deck or a screenshot set, small enough to keep one box. */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
export const Attachment = z.object({
    id: z.string(),
    room_id: z.string(),
    name: z.string(),
    content_type: z.string(),
    size: z.number().int(),
    /** Of the stored bytes, so the receiver can confirm they got what was sent. */
    sha256: z.string(),
    note: z.string().nullable(),
    /** False for formats the outbound scanner cannot read. Shown on the file. */
    scanned: z.boolean(),
    uploaded_by: z.string(),
    uploaded_by_name: z.string(),
    uploaded_at: z.string(),
    /** Null while only the uploader can see it. */
    published_at: z.string().nullable(),
});
export const OpenQuestion = z.object({
    message_id: z.string(),
    from_id: z.string(),
    from_name: z.string(),
    from_org: z.string(),
    from_agent: z.boolean(),
    /** Set when the asker is an agent whose operator has named it. */
    from_nickname: z.string().nullable().optional(),
    mentions: z.array(z.string()),
    body: z.string(),
    to_role: MessageTarget,
    domain: Authority.nullable(),
    created_at: z.string(),
    age_hours: z.number(),
});
/** Open questions grouped by the person who has to answer them. */
export const WaitingOn = z.object({
    user_id: z.string().nullable(),
    display_name: z.string(),
    org: z.string(),
    /** Null when nobody holds the area the question needs — nobody can answer it. */
    unassigned_domain: Authority.nullable(),
    count: z.number().int(),
    /** Of `count`, how many their Claude may answer without them. */
    answerable_by_agent: z.number().int(),
    oldest_age_hours: z.number(),
    questions: z.array(OpenQuestion),
});
/** A decision area with nobody responsible for it. */
export const AuthorityGap = z.object({
    domain: Authority,
    open_question_count: z.number().int(),
});
/**
 * One call that answers "今の仕様・実装どうなってますか？" — the PM's main view,
 * and what `cs_state` hands to an agent. Only approved content appears here.
 */
export const RoomState = z.object({
    room: z.object({ id: z.string(), name: z.string() }),
    generated_at: z.string(),
    latest_seq: z.number().int(),
    members: z.array(Member),
    reports: z.array(SessionReport),
    open_questions: z.array(OpenQuestion),
    /** The same questions, grouped by who has to answer — the PM's main view. */
    waiting_on: z.array(WaitingOn),
    /** Areas nobody is responsible for. Questions there cannot be routed. */
    authority_gaps: z.array(AuthorityGap),
    /** DEV / STG / PROD / APK — the addresses everyone keeps asking for. */
    links: z.array(EnvLink),
    /** Shared files. Unpublished ones appear only for whoever uploaded them. */
    files: z.array(Attachment),
    docs: z.array(DocMeta),
    recent_changes: z.array(DocVersion.extend({ path: z.string() })),
    decisions: z.array(Decision),
    pending_drafts_for_me: z.number().int(),
});
/* ---------------------------------------------------------------- invites */
export const RenameRoomRequest = z.object({
    name: z.string().min(1).max(120),
});
export const CreateInviteRequest = z.object({
    display_name: z.string().min(1).max(80),
    org: z.string().min(1).max(80),
    role: Role.default('member'),
    /** Assigned on redeem, so a new member is routable from their first minute. */
    authorities: z.array(Authority).default([]),
    expires_in_hours: z.number().int().min(1).max(720).default(168),
});
export const Invite = z.object({
    code: z.string(),
    room_id: z.string(),
    room_name: z.string(),
    display_name: z.string(),
    org: z.string(),
    role: Role,
    authorities: z.array(Authority),
    url: z.string(),
    expires_at: z.string(),
    redeemed_at: z.string().nullable(),
    revoked_at: z.string().nullable(),
});
/**
 * The rules a person accepts before joining.
 *
 * Kept as explicit ids rather than one boolean so the record says *what* was
 * agreed: the text will change, and a year from now "they ticked a box" is not an
 * answer to "did they know X".
 */
export const CONSENT_VERSION = '2026-09-17';
export const ConsentItem = z.enum([
    'no_credentials', // 認証情報を平文で書かない
    'no_personal_data', // 個人名・会社名などの個人情報を書かない
    'chat_is_immediate', // チャットの発言は承認なしで相手会社に届く
    'drafts_need_approval', // AI の要約は自分が承認するまで相手に渡らない
]);
export const CONSENT_TEXT = {
    no_credentials: 'ID・パスワード・APIキー等の認証情報を、平文で書いたり貼ったりしません。自動検出は入っていますが、検出漏れを前提にしません。',
    no_personal_data: '個人名・会社名などの個人情報を書きません。例: ×「N社の栗原さんに確認してください」→ ○「先方の担当者に確認してください」',
    chat_is_immediate: 'チャットに書いた内容は、承認を挟まずそのまま相手会社に届くことを理解しています。',
    drafts_need_approval: '自分の Claude が下書きしたセッション要約や仕様変更は、自分が承認するまで相手に渡らないことを理解しています。',
};
export const RedeemRequest = z.object({
    code: z.string().min(1),
    /** Lets the invitee correct the name the inviter typed. */
    display_name: z.string().min(1).max(80).optional(),
    /** Every item must be present; joining is refused otherwise. */
    accepted: z.array(ConsentItem).default([]),
});
/**
 * `token` is null when the invite was redeemed while already holding one: the
 * project is added to the existing identity and the caller keeps the token they
 * have. That is what lets a recurring group start a new project without everyone
 * reconfiguring their bridge.
 */
export const RedeemResult = z.object({
    token: z.string().nullable(),
    person: Person,
    room: z.object({ id: z.string(), name: z.string() }),
    role: Role,
    joined_existing: z.boolean(),
});
/* ------------------------------------------------------------ ws envelope */
export const StreamEvent = z.discriminatedUnion('type', [
    z.object({ type: z.literal('message'), message: Message }),
    z.object({ type: z.literal('message_resolved'), message_id: z.string(), resolved_by: z.string() }),
    z.object({
        type: z.literal('message_retracted'),
        message_id: z.string(),
        /** Absent when the message was purged outright and no longer exists. */
        message: Message.nullable(),
    }),
    z.object({ type: z.literal('report_retracted'), report_id: z.string() }),
    z.object({
        type: z.literal('draft'),
        draft: Draft,
        /**
         * The pending draft this one replaced, if any. A rolling session summary is
         * superseded rather than appended, and the old row is deleted — so a client that
         * only merges by id would keep showing a card whose approve button now 404s.
         */
        superseded: z.string().nullable().optional(),
    }),
    z.object({ type: z.literal('report'), report: SessionReport }),
    z.object({ type: z.literal('doc'), doc: DocMeta }),
    z.object({ type: z.literal('link'), link: EnvLink }),
    z.object({ type: z.literal('file'), file: Attachment }),
    z.object({ type: z.literal('file_removed'), file_id: z.string() }),
    z.object({ type: z.literal('link_removed'), link_id: z.string() }),
    z.object({ type: z.literal('decision'), decision: Decision }),
    z.object({
        type: z.literal('hello'),
        latest_seq: z.number().int(),
        user_id: z.string(),
        /**
         * Identifies the web build this hub serves. A client that sees it change across
         * reconnects is running superseded JavaScript and should reload — otherwise it
         * renders new data with old code and silently misleads whoever is reading it.
         */
        web_version: z.string().optional(),
    }),
    z.object({ type: z.literal('ping') }),
]);
/* ----------------------------------------------------------------- errors */
export const ApiError = z.object({
    error: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
});
//# sourceMappingURL=schemas.js.map